import { AriaRole, ReactNode, useState } from "react";
import cx from "classnames";

import { Space } from "components/icons/generated";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import Link from "ds/components/Link";
import Typography from "ds/components/Typography";
import { ariaCellInteractionProps } from "ds/components/Table/hooks/useKeyboardNavigation/ariaCellInteractionProps";
import { AnalyticsCommonProps } from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { IconComponent } from "types/Icon";
import { getSpacePathSegments, SpacePathSegment } from "utils/space";
import { SpacesContext } from "views/Account/SpacesProvider";

import { ELLIPSIS, SEPARATOR, SpacePathLayout, toWrappablePath } from "./helpers";
import useSpacePathLayout from "./useSpacePathLayout";
import styles from "./styles.module.css";

type SpacePathVariant = "meta" | "inline" | "static" | "expandable";

type SpacePathProps = {
  spaceId?: string;
  fallbackName?: string;
  variant?: SpacePathVariant;
  className?: string;
  role?: AriaRole;
  disabled?: boolean;
  tooltip?: ReactNode;
  target?: string;
  icon?: IconComponent;
} & AnalyticsCommonProps;

const SpacePath = ({
  spaceId,
  fallbackName,
  variant,
  className,
  role,
  disabled,
  tooltip,
  target,
  icon: iconSrc,
  analyticsPage,
  analyticsTitle,
  analyticsProps,
}: SpacePathProps) => {
  const { spaceHierarchy, spaces } = useTypedContext(SpacesContext);
  const [isExpanded, setIsExpanded] = useState(false);

  const segments = spaceId ? getSpacePathSegments(spaceId, spaceHierarchy, spaces) : [];

  const fallbackSegments: SpacePathSegment[] = spaceId
    ? [{ id: spaceId, name: fallbackName ?? spaceId }]
    : [];

  const resolvedSegments = segments.length > 0 ? segments : fallbackSegments;

  const names = resolvedSegments.map((segment) => segment.name);
  const shouldMeasureLayout = variant !== "static" && !(variant === "expandable" && isExpanded);

  const { ref, layout } = useSpacePathLayout(names, shouldMeasureLayout);

  if (!spaceId) {
    return null;
  }

  const lastSegment = resolvedSegments[resolvedSegments.length - 1];
  const fullPath = names.join(SEPARATOR);
  const isInline = variant === "inline";
  const isDefault = !variant;
  const isExpandable = variant === "expandable";
  const showExpandedPath = isExpandable && isExpanded;

  // Only `meta` carries the default space icon — it sits in a meta info list where every
  // item is icon-led. Other variants render one only when the caller passes it.
  const resolvedIcon = iconSrc ?? (variant === "meta" ? Space : undefined);
  const icon = resolvedIcon && <Icon src={resolvedIcon} className={styles.icon} aria-hidden />;

  const renderName = (name: string, ellipsize: boolean) => {
    const nameClassName = cx(styles.name, ellipsize && styles.ellipsize);

    return isInline ? (
      <span className={nameClassName}>{name}</span>
    ) : (
      <Typography tag="span" variant="p-body3" className={nameClassName}>
        {name}
      </Typography>
    );
  };

  const renderSegments = ({ leadingEllipsis, segments: parts, ellipsizeLast }: SpacePathLayout) => (
    <>
      {leadingEllipsis && <span className={styles.separator}>{`${ELLIPSIS}${SEPARATOR}`}</span>}
      {parts.map((name, index) => (
        <span
          key={`${index}-${name}`}
          className={cx(
            styles.segment,
            index === parts.length - 1 && ellipsizeLast && styles.segmentEllipsis
          )}
        >
          {index > 0 && <span className={styles.separator}>{SEPARATOR}</span>}
          {renderName(name, index === parts.length - 1 && ellipsizeLast)}
        </span>
      ))}
    </>
  );

  // The whole path is a single link to the direct (last) space. It has to stay on the DS Link
  // (react-aria) rather than BaseAction: the default variant lives in a react-aria grid whose
  // keyboard navigation relies on `ariaCellInteractionProps`, which only works with Link.
  const renderLink = (content: ReactNode, pathTooltip?: string) => {
    // A caller tooltip (e.g. "you don't have access") and the truncated path are both worth
    // reading, so stack them instead of letting one hide the other.
    const linkTooltip =
      tooltip && pathTooltip ? (
        <>
          {pathTooltip}
          <br />
          {tooltip}
        </>
      ) : (
        (tooltip ?? pathTooltip)
      );

    return (
      <Link
        href={`/spaces/${lastSegment.id}`}
        variant="primary"
        size={isDefault ? "small" : "inherit"}
        inheritFont={!isDefault}
        noEllipsis
        disabled={disabled}
        target={target}
        tooltip={linkTooltip}
        tooltipWidthMode="maxWidthXl"
        className={styles.link}
        // The rendered text is abbreviated when truncated and zero-width-space-joined when
        // expanded, so it can't serve as the accessible name.
        aria-label={fullPath}
        analyticsPage={analyticsPage}
        analyticsTitle={analyticsTitle}
        analyticsProps={analyticsProps}
        {...(isDefault ? ariaCellInteractionProps : {})}
      >
        {content}
      </Link>
    );
  };

  const renderWrapper = (children: ReactNode) =>
    isInline ? (
      <span role={role} data-space-path className={cx(styles.wrapper, className)}>
        {children}
      </span>
    ) : (
      <Typography
        tag="span"
        variant="p-body3"
        role={role}
        data-space-path
        className={cx(
          styles.wrapper,
          isDefault && styles.default,
          variant && styles[variant],
          showExpandedPath && styles.expanded,
          className
        )}
      >
        {children}
      </Typography>
    );

  // `static` only shows the direct space as plain (non-link) text — e.g. a grant on the space
  // currently being viewed, or a dropdown item whose parent already navigates.
  if (variant === "static") {
    return renderWrapper(
      <>
        {icon}
        {renderName(lastSegment.name, true)}
      </>
    );
  }

  // `expandable` truncates to the available width, with a button to reveal the wrapped full path.
  if (isExpandable) {
    if (showExpandedPath) {
      return renderWrapper(
        <>
          <span className={styles.row}>
            {icon}
            <span className={styles.path}>{renderLink(toWrappablePath(names))}</span>
          </span>
          <Button
            variant="secondary"
            size="small"
            className={styles.showFullPath}
            onPress={() => setIsExpanded(false)}
          >
            Hide full path
          </Button>
        </>
      );
    }

    return renderWrapper(
      <>
        <span className={styles.row}>
          {icon}
          <span ref={ref} className={styles.path}>
            {renderLink(renderSegments(layout), layout.truncated ? fullPath : undefined)}
          </span>
        </span>
        {layout.truncated && (
          <Button
            variant="secondary"
            size="small"
            className={styles.showFullPath}
            onPress={() => setIsExpanded(true)}
          >
            Show full path
          </Button>
        )}
      </>
    );
  }

  return renderWrapper(
    <>
      {icon}
      <span ref={ref} className={styles.path}>
        {renderLink(renderSegments(layout), layout.truncated ? fullPath : undefined)}
      </span>
    </>
  );
};

export default SpacePath;
